import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../services/api';
import {
  Course,
  MeResponse,
  OrganisationClass,
  OrganisationManagement,
  OrganisationMember,
} from '../../types';
import Navbar from '../shared/Navbar';

interface PickerOption {
  id: number;
  label: string;
  description?: string;
}

const AutocompletePicker: React.FC<{
  options: PickerOption[];
  placeholder: string;
  disabled: boolean;
  onSelect: (id: number) => void;
  onCancel: () => void;
}> = ({ options, placeholder, disabled, onSelect, onCancel }) => {
  const [query, setQuery] = useState('');
  const resultsId = React.useId();
  const filteredOptions = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    const matches = normalized
      ? options.filter(option =>
          `${option.label} ${option.description ?? ''}`.toLocaleLowerCase().includes(normalized))
      : options;
    return matches.slice(0, 8);
  }, [options, query]);

  return (
    <div className="org-picker">
      <input
        type="search"
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder={placeholder}
        autoFocus
        disabled={disabled}
        role="combobox"
        aria-autocomplete="list"
        aria-controls={resultsId}
        aria-expanded={filteredOptions.length > 0}
      />
      <button type="button" className="tm-button tm-button--secondary org-secondary-btn" onClick={onCancel}>Cancel</button>
      <div className="org-picker-results" role="listbox" id={resultsId}>
        {filteredOptions.length === 0 ? (
          <div className="org-picker-empty">No matches</div>
        ) : filteredOptions.map(option => (
          <button
            key={option.id}
            type="button"
            role="option"
            aria-selected="false"
            onClick={() => onSelect(option.id)}
            disabled={disabled}
          >
            <span>{option.label}</span>
            {option.description && <small>{option.description}</small>}
          </button>
        ))}
      </div>
    </div>
  );
};

const ManageOrganisationPage: React.FC = () => {
  const { organisationId: organisationIdParam } = useParams();
  const organisationId = Number(organisationIdParam);
  const validOrganisationId = Number.isInteger(organisationId) && organisationId > 0;
  const [me, setMe] = useState<MeResponse | null>(null);
  const [management, setManagement] = useState<OrganisationManagement | null>(null);
  const [newClassName, setNewClassName] = useState('');
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteRole, setInviteRole] = useState<'STUDENT' | 'TEACHER'>('STUDENT');
  const [inviteClassIds, setInviteClassIds] = useState<number[]>([]);
  const [inviting, setInviting] = useState(false);
  const emailsToInvite = useMemo(() => Array.from(new Set(
    inviteEmails.toLowerCase().split(/[\s,;]+/).filter(Boolean),
  )), [inviteEmails]);
  const [inviteResults, setInviteResults] = useState<Awaited<ReturnType<typeof api.createOrganisationUsers>>['results']>([]);

  const [userPickerClassId, setUserPickerClassId] = useState<number | null>(null);
  const [coursePickerClassId, setCoursePickerClassId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadManagement = useCallback(async () => {
    if (!validOrganisationId) return;
    const response = await api.getOrganisationManagement(organisationId);
    setManagement(response);
    setInviteClassIds(current => current.filter(classId => response.classes.some(item => item.id === classId)));
  }, [organisationId, validOrganisationId]);

  useEffect(() => {
    const bootstrap = async () => {
      if (!validOrganisationId) {
        setError('Invalid organisation');
        setLoading(false);
        return;
      }
      try {
        const current = await api.me();
        setMe(current);
        if (!current.authenticated) return;
        const canOpen = current.user.is_admin || (
          current.user.organisation_id === organisationId
          && (current.user.organisation_role === 'ADMIN' || current.user.organisation_role === 'TEACHER')
        );
        if (!canOpen) return;
        await loadManagement();
      } catch (err: any) {
        setError(err?.message || 'Failed to load organisation management');
      } finally {
        setLoading(false);
      }
    };
    void bootstrap();
  }, [loadManagement, organisationId, validOrganisationId]);

  const runMutation = async (operation: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await operation();
    } catch (err: any) {
      setError(err?.message || 'The update failed');
    } finally {
      setBusy(false);
    }
  };

  const replaceClass = (updated: OrganisationClass) => {
    setManagement(current => current
      ? {
          ...current,
          classes: current.classes.map(item => item.id === updated.id ? updated : item),
        }
      : current);
  };

  const handleCreateUsers = (event: FormEvent) => {
    event.preventDefault();
    const emails = emailsToInvite;
    if (!management?.can_edit_classes || !emails.length || emails.length > 1000) return;
    setInviteResults([]);
    void runMutation(async () => {
      setInviting(true);
      try {
        const response = await api.createOrganisationUsers(organisationId, emails, inviteRole, inviteClassIds);
        setInviteResults(response.results);
        setInviteEmails(response.results
          .filter(result => result.status === 'failed' || result.status === 'email_failed')
          .map(result => result.email).join('\n'));
        await loadManagement();
      } finally {
        setInviting(false);
      }
    });
  };

  const handleCreateClass = (event: FormEvent) => {
    event.preventDefault();
    const name = newClassName.trim();
    if (!management?.can_edit_classes || !name) return;
    void runMutation(async () => {
      await api.createOrganisationClass(organisationId, name);
      setNewClassName('');
      await loadManagement();
    });
  };

  const handleRenameClass = (organisationClass: OrganisationClass) => {
    const name = window.prompt('Class name', organisationClass.name)?.trim();
    if (!name || name === organisationClass.name) return;
    void runMutation(async () => {
      const response = await api.updateOrganisationClass(organisationClass.id, name);
      replaceClass(response.class);
    });
  };

  const handleDeleteClass = (organisationClass: OrganisationClass) => {
    if (!window.confirm(`Delete ${organisationClass.name}?`)) return;
    void runMutation(async () => {
      await api.deleteOrganisationClass(organisationClass.id);
      await loadManagement();
    });
  };

  const handleAddUser = (organisationClass: OrganisationClass, userId: number) => {
    void runMutation(async () => {
      const response = await api.addClassUser(organisationClass.id, userId);
      replaceClass(response.class);
      setUserPickerClassId(null);
    });
  };

  const handleRemoveUser = (organisationClass: OrganisationClass, userId: number) => {
    void runMutation(async () => {
      const response = await api.removeClassUser(organisationClass.id, userId);
      replaceClass(response.class);
    });
  };

  const handleAddCourse = (organisationClass: OrganisationClass, courseId: number) => {
    void runMutation(async () => {
      const response = await api.grantClassCourse(organisationClass.id, courseId);
      replaceClass(response.class);
      setCoursePickerClassId(null);
    });
  };

  const handleRemoveCourse = (organisationClass: OrganisationClass, courseId: number) => {
    void runMutation(async () => {
      const response = await api.revokeClassCourse(organisationClass.id, courseId);
      replaceClass(response.class);
    });
  };

  const memberById = useMemo(
    () => new Map((management?.members ?? []).map(member => [member.id, member])),
    [management?.members],
  );
  const courseById = useMemo(
    () => new Map((management?.courses ?? []).map(course => [course.id, course])),
    [management?.courses],
  );
  const assignableMembers = useMemo(
    () => (management?.members ?? []).filter(member =>
      member.organisation_role === 'STUDENT' || member.organisation_role === 'TEACHER'),
    [management?.members],
  );

  const signedInCanOpen = Boolean(me?.authenticated && (
    me.user.is_admin || (
      me.user.organisation_id === organisationId
      && (me.user.organisation_role === 'ADMIN' || me.user.organisation_role === 'TEACHER')
    )
  ));

  const renderMember = (
    organisationClass: OrganisationClass,
    member: OrganisationMember | undefined,
    userId: number,
  ) => {
    if (!management?.can_edit_classes) {
      return (
        <Link
          className="org-assignment-item org-student-card org-student-card-link"
          key={userId}
          to={`/manage/user/${userId}`}
        >
          <span>
            {member?.email ?? `User #${userId}`}
            {member?.organisation_role && <small>{member.organisation_role}</small>}
          </span>
        </Link>
      );
    }

    return (
      <div className="org-assignment-item" key={userId}>
        <span>
          <Link className="managed-user-link" to={`/manage/user/${userId}`}>
            {member?.email ?? `User #${userId}`}
          </Link>
          {member?.organisation_role && <small>{member.organisation_role}</small>}
        </span>
        <button
          type="button"
          onClick={() => handleRemoveUser(organisationClass, userId)}
          disabled={busy}
          aria-label={`Remove ${member?.email ?? `user ${userId}`}`}
        >
          ×
        </button>
      </div>
    );
  };

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="content-container organisation-admin organisation-manager">
          <div className="page-intro org-page-heading">
            <h2>{management?.organisation.name ?? 'Manage organisation'}</h2>
            <p>
              {management?.can_edit_classes
                ? 'Manage your people, classes and course access.'
                : 'View students in the classes you teach.'}
            </p>
          </div>

          {error && <div className="org-error" role="alert">{error}</div>}

          {!loading && validOrganisationId && !signedInCanOpen && (
            <div className="admin-access-warning">
              <strong>Organisation access required</strong>
              <span>You must be an administrator or teacher for this organisation.</span>
              <div>
                {!me?.authenticated && <Link className="tm-button tm-button--primary ml-replace-btn" to="/login">Sign In</Link>}
                <Link className="tm-button tm-button--secondary org-secondary-btn" to="/dashboard">Back to Dashboard</Link>
              </div>
            </div>
          )}

          {loading && <div className="ml-subtle">Loading organisation…</div>}

          {management?.can_edit_classes && (
            <details className="tm-surface org-invite-panel">
              <summary>
                <span className="org-invite-icon" aria-hidden="true">+</span>
                <span className="org-invite-title">
                  <strong>Invite users</strong>
                  <span>Invite students or teachers with a welcome email.</span>
                </span>
                <span className="org-invite-toggle" aria-hidden="true">⌄</span>
              </summary>
              <div className="org-invite-body">
                <form onSubmit={handleCreateUsers} aria-busy={inviting}>
                  <div className="form-row org-invite-role">
                    <label htmlFor="invite-role">Role for new users</label>
                    <select id="invite-role" value={inviteRole} disabled={busy}
                      onChange={event => setInviteRole(event.target.value as 'STUDENT' | 'TEACHER')}>
                      <option value="STUDENT">Student</option>
                      <option value="TEACHER">Teacher</option>
                    </select>
                  </div>
                  <fieldset className="org-invite-classes" disabled={busy} aria-describedby="invite-classes-help">
                    <legend>Assign classes <span>(optional)</span></legend>
                    {management.classes.length > 0 ? (
                      <>
                        <p id="invite-classes-help" className="org-field-help">Add every new or pending user in this batch to the selected classes. Existing memberships are kept.</p>
                        <div className="org-invite-class-options">
                          {management.classes.map(organisationClass => (
                            <label key={organisationClass.id}>
                              <input type="checkbox" checked={inviteClassIds.includes(organisationClass.id)}
                                onChange={event => {
                                  const checked = event.target.checked;
                                  setInviteClassIds(current => checked
                                    ? [...current, organisationClass.id]
                                    : current.filter(classId => classId !== organisationClass.id));
                                }} />
                              <span>{organisationClass.name}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p id="invite-classes-help" className="org-field-help">Create a class below to assign users as you invite them.</p>
                    )}
                  </fieldset>
                  <div className="form-row">
                    <label htmlFor="invite-emails">Email addresses</label>
                    <p id="invite-help" className="org-field-help">Paste up to 1,000 emails, separated by new lines, commas or spaces.</p>
                    <textarea id="invite-emails" rows={5} value={inviteEmails}
                      aria-describedby="invite-help invite-count" aria-invalid={emailsToInvite.length > 1000}
                      onChange={event => setInviteEmails(event.target.value)}
                      placeholder={'student@example.com\nstudent2@example.com'} required disabled={busy} />
                  </div>
                  <div className="org-invite-footer">
                    <span id="invite-count" className={emailsToInvite.length > 1000 ? 'org-count-error' : ''} aria-live="polite">
                      {emailsToInvite.length.toLocaleString()} / 1,000 emails
                      {emailsToInvite.length > 1000 && ' — split this list into smaller batches.'}
                    </span>
                    <button className="tm-button tm-button--primary" type="submit" disabled={busy || !emailsToInvite.length || emailsToInvite.length > 1000}>
                      {inviting ? 'Sending invitations…' : 'Send invitations'}
                    </button>
                  </div>
                  <p className="org-field-help org-invite-note">Each new user receives a link to create their password. To resend a pending invitation, enter their email again. Resending keeps their existing role.</p>
                </form>
                {inviteResults.length > 0 && (
                  <div className="org-invite-results">
                    <p role="status">
                      <strong>{inviteResults.filter(result => result.status === 'created' || result.status === 'resent').length.toLocaleString()} sent</strong>
                      <span>{inviteResults.filter(result => result.status === 'skipped').length.toLocaleString()} existing accounts skipped</span>
                      <span>{inviteResults.filter(result => result.status === 'failed' || result.status === 'email_failed').length.toLocaleString()} need retrying</span>
                    </p>
                    <details>
                      <summary>View results for each email</summary>
                      <ul>
                        {inviteResults.map(result => <li key={result.email}>
                          <strong>{result.email}</strong>
                          <span>{{
                            created: 'Account created · Invitation sent',
                            resent: 'Invitation resent',
                            skipped: 'Existing account skipped',
                            failed: 'Could not create user. Please retry.',
                            email_failed: 'Email delivery failed. Please retry to resend.',
                          }[result.status]}</span>
                        </li>)}
                      </ul>
                    </details>
                  </div>
                )}
              </div>
            </details>
          )}

          {management && (
            <section className="tm-surface org-detail-panel">
              {management.can_edit_classes && (
                <div className="org-classes-header">
                  <div className="org-classes-title"><h3>Classes <span>{management.classes.length}</span></h3><p>Organise members and assign courses.</p></div>
                  <form className="org-inline-form" onSubmit={handleCreateClass}>
                    <input
                      value={newClassName}
                      onChange={event => setNewClassName(event.target.value)}
                      aria-label="New class name"
                      placeholder="New class name"
                      maxLength={255}
                      disabled={busy}
                    />
                    <button className="tm-button tm-button--primary ml-replace-btn" type="submit" disabled={busy || !newClassName.trim()}>
                      Add class
                    </button>
                  </form>
                </div>
              )}

              {!management.can_edit_classes && <h3 className="org-readonly-heading">Your classes <span className="org-class-count">{management.classes.length}</span></h3>}

              {management.classes.length === 0 ? (
                <div className="ml-empty org-classes-empty">
                  {management.can_edit_classes
                    ? 'No classes in this organisation.'
                    : 'You are not assigned to any classes.'}
                </div>
              ) : (
                <div className="org-class-list">
                  {management.classes.map(organisationClass => {
                    const classMembers = organisationClass.user_ids
                      .map(userId => memberById.get(userId))
                      .filter((member): member is OrganisationMember => Boolean(member));
                    const students = classMembers.filter(member => member.organisation_role === 'STUDENT');
                    return (
                      <article className="tm-surface org-class-card" key={organisationClass.id}>
                        <header>
                          <div>
                            <h4>{organisationClass.name}</h4>
                            <span>
                              {management.can_edit_classes
                                ? `${organisationClass.user_ids.length} users · ${organisationClass.course_ids.length} courses`
                                : `${students.length} students`}
                            </span>
                          </div>
                          {management.can_edit_classes && (
                            <div className="org-card-actions">
                              <button type="button" className="tm-button tm-button--secondary org-secondary-btn" onClick={() => handleRenameClass(organisationClass)} disabled={busy}>
                                Rename
                              </button>
                              <button type="button" className="tm-button tm-button--danger ml-delete-btn" onClick={() => handleDeleteClass(organisationClass)} disabled={busy}>
                                Delete
                              </button>
                            </div>
                          )}
                        </header>
                        <div className={`org-class-columns${management.can_edit_classes ? '' : ' org-class-columns--readonly'}`}>
                          <div>
                            <h5>{management.can_edit_classes ? 'Class members' : 'Students'}</h5>
                            {organisationClass.user_ids.length === 0 ? (
                              <p className="ml-subtle">{management.can_edit_classes ? 'No members yet.' : 'No students in this class.'}</p>
                            ) : (
                              <div className={`org-assignment-list${management.can_edit_classes ? '' : ' org-student-card-list'}`}>
                                {organisationClass.user_ids.map(userId =>
                                  renderMember(organisationClass, memberById.get(userId), userId))}
                              </div>
                            )}
                            {management.can_edit_classes && (
                              userPickerClassId === organisationClass.id ? (
                                <AutocompletePicker
                                  options={assignableMembers
                                    .filter(member => !organisationClass.user_ids.includes(member.id))
                                    .map(member => ({
                                      id: member.id,
                                      label: member.email,
                                      description: member.organisation_role,
                                    }))}
                                  placeholder="Search by email"
                                  disabled={busy}
                                  onSelect={userId => handleAddUser(organisationClass, userId)}
                                  onCancel={() => setUserPickerClassId(null)}
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="org-add-assignment-btn"
                                  disabled={busy || assignableMembers.every(member => organisationClass.user_ids.includes(member.id))}
                                  onClick={() => {
                                    setUserPickerClassId(organisationClass.id);
                                    setCoursePickerClassId(null);
                                  }}
                                >
                                  Add user
                                </button>
                              )
                            )}
                          </div>
                          {management.can_edit_classes && (
                            <div>
                              <h5>Course access</h5>
                              {organisationClass.course_ids.length === 0 ? (
                                <p className="ml-subtle">No courses assigned to this class.</p>
                              ) : (
                                <div className="org-assignment-list">
                                  {organisationClass.course_ids.map(courseId => {
                                    const course: Course | undefined = courseById.get(courseId);
                                    return (
                                      <div className="org-assignment-item" key={courseId}>
                                        <span>
                                          <Link className="org-course-link" to={`/course/${courseId}`}>
                                            {course?.title ?? `Course #${courseId}`}
                                          </Link>
                                          <small>{course?.hidden ? 'Hidden course' : 'Public course'}</small>
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveCourse(organisationClass, courseId)}
                                          disabled={busy}
                                          aria-label={`Remove ${course?.title ?? `course ${courseId}`}`}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {coursePickerClassId === organisationClass.id ? (
                                <AutocompletePicker
                                  options={management.courses
                                    .filter(course => !organisationClass.course_ids.includes(course.id))
                                    .map(course => ({
                                      id: course.id,
                                      label: `${course.title} (#${course.id})`,
                                      description: course.hidden ? 'Hidden course' : 'Public course',
                                    }))}
                                  placeholder="Search courses"
                                  disabled={busy}
                                  onSelect={courseId => handleAddCourse(organisationClass, courseId)}
                                  onCancel={() => setCoursePickerClassId(null)}
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="org-add-assignment-btn"
                                  disabled={busy || management.courses.every(course => organisationClass.course_ids.includes(course.id))}
                                  onClick={() => {
                                    setCoursePickerClassId(organisationClass.id);
                                    setUserPickerClassId(null);
                                  }}
                                >
                                  Add course
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default ManageOrganisationPage;
